// frontend/src/components/LandingPage/DemoSection.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
    Monitor, 
    Smartphone, 
    Tablet, 
    CheckCircle2,
    ArrowRight,
    Play
} from 'lucide-react';
import PrimaryButton from '../Common/PrimaryButton';
import { Link } from 'react-router-dom';

const features = [
    {
        title: 'Interface Intuitiva',
        description: 'Navegação simples e direta para qualquer perfil de usuário',
        icon: CheckCircle2
    },
    {
        title: '100% Responsivo',
        description: 'Acesse de qualquer dispositivo: desktop, tablet ou mobile',
        icon: CheckCircle2
    },
    {
        title: 'Tempo Real',
        description: 'Dados atualizados instantaneamente em todos os dispositivos',
        icon: CheckCircle2
    },
    {
        title: 'Segurança Avançada',
        description: 'Criptografia de ponta e conformidade com LGPD',
        icon: CheckCircle2
    }
];

const DemoSection = () => {
    const [hoveredDevice, setHoveredDevice] = useState('desktop');

    return (
        <section id="demo-section" className="py-20 sm:py-24 bg-white relative overflow-hidden">
            {/* Background decorativo */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, #1A5B4C 1px, transparent 0)`,
                    backgroundSize: '30px 30px'
                }} />
            </div>

            <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Coluna Esquerda: Texto e Features */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
                            Veja o{' '}
                            <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                Higeia.tech
                            </span>{' '}
                            em ação
                        </h2>
                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Uma plataforma moderna, intuitiva e poderosa. Experimente a diferença que a tecnologia pode fazer na gestão de saúde.
                        </p>

                        {/* Lista de Features */}
                        <div className="space-y-4 mb-8">
                            {features.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-start gap-4"
                                    >
                                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-higeia-light flex items-center justify-center">
                                            <Icon className="h-6 w-6 text-higeia-dark" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{feature.title}</h3>
                                            <p className="text-gray-600">{feature.description}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* CTA */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Link to="/demonstracao">
                                <PrimaryButton className="px-8 py-4 text-lg font-bold bg-higeia-dark hover:bg-higeia-medium shadow-xl">
                                    Experimente Grátis por 30 dias
                                    <ArrowRight className="h-5 w-5 ml-2" />
                                </PrimaryButton>
                            </Link>
                        </motion.div>
                    </motion.div>

                    {/* Coluna Direita: Mockup/Visualização */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative"
                    >
                        {/* Container do Mockup */}
                        <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-8 shadow-2xl">
                            {/* Seletor de Dispositivo */}
                            <div className="flex justify-center gap-4 mb-6">
                                {[
                                    { id: 'desktop', icon: Monitor, label: 'Desktop' },
                                    { id: 'tablet', icon: Tablet, label: 'Tablet' },
                                    { id: 'mobile', icon: Smartphone, label: 'Mobile' }
                                ].map((device) => {
                                    const Icon = device.icon;
                                    return (
                                        <button
                                            key={device.id}
                                            onClick={() => setHoveredDevice(device.id)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                                                hoveredDevice === device.id
                                                    ? 'bg-higeia-dark text-white shadow-lg'
                                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Icon className="h-5 w-5" />
                                            <span className="text-sm font-medium">{device.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Mockup do Dashboard */}
                            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
                                {/* Barra superior do navegador */}
                                <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-400" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                                    <div className="w-3 h-3 rounded-full bg-green-400" />
                                </div>

                                {/* Conteúdo do mockup */}
                                <div className="p-6">
                                    {/* Header do Dashboard */}
                                    <div className="mb-6">
                                        <div className="h-8 bg-higeia-dark rounded-lg w-1/3 mb-2" />
                                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                                    </div>

                                    {/* Cards de métricas */}
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        {[1, 2, 3, 4].map((i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                whileInView={{ opacity: 1, scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: i * 0.1 }}
                                                className="bg-gradient-to-br from-higeia-light to-white p-4 rounded-xl border border-higeia-light/50"
                                            >
                                                <div className="h-3 bg-gray-300 rounded w-1/2 mb-2" />
                                                <div className="h-6 bg-higeia-dark rounded w-3/4" />
                                            </motion.div>
                                        ))}
                                    </div>

                                    {/* Gráfico placeholder */}
                                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                        <div className="h-32 bg-gradient-to-t from-higeia-dark/20 to-transparent rounded-lg flex items-end justify-around gap-2">
                                            {[40, 60, 45, 80, 70, 90].map((height, i) => (
                                                <motion.div
                                                    key={i}
                                                    initial={{ height: 0 }}
                                                    whileInView={{ height: `${height}%` }}
                                                    viewport={{ once: true }}
                                                    transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                                                    className="bg-higeia-dark rounded-t w-full"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Badge de "Interface Intuitiva" */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.8 }}
                                className="absolute -bottom-4 -right-4 bg-white px-4 py-2 rounded-full shadow-xl border-2 border-higeia-light flex items-center gap-2"
                            >
                                <Play className="h-5 w-5 text-higeia-dark" />
                                <span className="font-semibold text-gray-900">Interface Intuitiva</span>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default DemoSection;

